import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useQueryParam, NumberParam, StringParam } from "use-query-params";
import {
  FilterBar,
  Table,
  TableSkeleton,
  TopProgress,
} from "../../../components";
import {
  Container,
  WrapperRow,
  Title,
  BodyText,
  GreenDot,
  Greentext,
  StateWrapper,
} from "../../cloud/Instances/styles";
import { utilsService } from "../../../api";
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import { handleStatusName } from "../../../utils/handle-status-name";
import { icons } from "../../../icons";
import { useUi } from "../../../context/ui.context";
import {
  EmptyValue,
  Mono,
  SecondaryValue,
  Stack,
  Value,
} from "./styles";

const DEFAULT_ORDERING = "-created_at";

type TaskLogItem = {
  id: string;
  parent_id: string | null;
  name: string | null;
  args: string | null;
  kwargs: string | null;
  user: {
    id?: number | string | null;
    full_name?: string | null;
    email?: string | null;
  } | null;
  state: string | null;
  created_at: string | null;
  last_update: string | null;
  resource_id: string | number | null;
  resource_type: string | null;
  description: string | null;
  has_subtasks: boolean;
  operations?: any[];
};

const PeriodicTaskLogPage: FC = () => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const fetchLogsRef = useRef<() => void>(() => {});
  const [page] = useQueryParam("page", NumberParam);
  const [ordering, setOrdering] = useQueryParam("ordering", StringParam);
  const [pageSize] = useQueryParam("page_size", NumberParam);
  const [filtering] = useQueryParam("filtering", StringParam);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [logs, setLogs] = useState<any[]>([]);
  const [paginationData, setPaginationData] = useState<any>();
  const [filterOptions, setFilterOptions] = useState<any>();
  const [recreateLoadingId, setRecreateLoadingId] = useState<string | null>(null);

  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);

  const renderState = useCallback(
    (state?: string | null) => {
      if (!state) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      return (
        <StateWrapper>
          <GreenDot active={state} />
          <Greentext style={{ textTransform: "capitalize" }} active={state}>
            {handleStatusName(state, t)}
          </Greentext>
        </StateWrapper>
      );
    },
    [t]
  );

  const renderTask = useCallback(
    (task: TaskLogItem) => {
      if (!task.name && !task.description) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      return (
        <Stack>
          {task.name ? <Value title={task.name}>{task.name}</Value> : null}
          {task.description ? (
            <SecondaryValue title={task.description}>
              {task.description}
            </SecondaryValue>
          ) : null}
        </Stack>
      );
    },
    [t]
  );

  const renderPayload = useCallback(
    (args?: string | null, kwargs?: string | null) => {
      if (!args && !kwargs) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      return (
        <Stack>
          {args ? (
            <Stack>
              <SecondaryValue>{t("ARGS")}</SecondaryValue>
              <Mono>{args}</Mono>
            </Stack>
          ) : null}
          {kwargs ? (
            <Stack>
              <SecondaryValue>{t("KWARGS")}</SecondaryValue>
              <Mono>{kwargs}</Mono>
            </Stack>
          ) : null}
        </Stack>
      );
    },
    [t]
  );

  const renderResource = useCallback(
    (task: TaskLogItem) => {
      if (!task.resource_type && !task.resource_id) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      return (
        <Stack>
          {task.resource_type ? (
            <Value title={task.resource_type}>{task.resource_type}</Value>
          ) : null}
          {task.resource_id ? (
            <SecondaryValue>#{task.resource_id}</SecondaryValue>
          ) : null}
        </Stack>
      );
    },
    [t]
  );

  const handleRecreate = useCallback(
    async (taskId: string) => {
      if (!taskId) {
        return;
      }
      setRecreateLoadingId(taskId);
      try {
        const response = await utilsService.recreatePeriodicTaskLog(taskId, {});
        viewAlert({
          severity: "success",
          message:
            response?.data?.detail || t("TASK_RECREATE_TRIGGERED"),
        });
        fetchLogsRef.current();
      } catch (error: any) {
        viewAlert({
          severity: "error",
          message:
            error?.response?.data?.detail || t("TASK_RECREATE_FAILED"),
        });
      } finally {
        setRecreateLoadingId(null);
      }
    },
    [t, viewAlert]
  );

  const toTableRow = useCallback(
    (task: TaskLogItem) => {
      const taskUuid = task.id;
      const dropOptions =
        task.state && task.state.toLowerCase() !== "success"
          ? [
              {
                title: t("RECREATE_TASK"),
                id: "recreate",
                icon: icons.Restart,
                divider: false,
                disabled: recreateLoadingId === taskUuid,
                onClick: (item: any) =>
                  handleRecreate(item.taskUuid || taskUuid),
              },
            ]
          : [];

      return {
        ...task,
        taskUuid,
        navigate: `/utilities/periodic-task-log/${taskUuid}`,
        parent_task: task.parent_id ? (
          <Value title={task.parent_id}>{task.parent_id}</Value>
        ) : (
          <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>
        ),
        created_at: task.created_at ? (
          timeConverterMMDDYYHHMM(task.created_at)
        ) : (
          <EmptyValue>—</EmptyValue>
        ),
        last_update: task.last_update ? (
          timeConverterMMDDYYHHMM(task.last_update)
        ) : (
          <EmptyValue>—</EmptyValue>
        ),
        task: renderTask(task),
        payload: renderPayload(task.args, task.kwargs),
        resource: renderResource(task),
        state: renderState(task.state),
        dropOptions,
        hideProps: dropOptions.length === 0,
      };
    },
    [
      handleRecreate,
      renderPayload,
      renderResource,
      renderState,
      renderTask,
      recreateLoadingId,
      t,
    ]
  );

  const fetchLogs = useCallback(() => {
    const currentPage = page || 1;
    const currentPageSize = pageSize || 10;
    const currentOrdering = ordering || DEFAULT_ORDERING;

    // setPageLoading(true);
    utilsService
      .getPeriodicTaskLog({
        page: currentPage,
        pageSize: currentPageSize,
        ordering: currentOrdering,
        filtering: filtering || undefined,
      })
      .then((res) => {
        const items: TaskLogItem[] = res.data?.objects || [];
        setLogs(items.map((item) => toTableRow(item)));
        setPaginationData(res.data);
        setFilterOptions(res.data?.filter_options);
      })
      .catch((error) => {
        console.error("Failed to fetch periodic task log", error);
        setLogs([]);
        setPaginationData(undefined);
        setFilterOptions(undefined);
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [filtering, ordering, page, pageSize, toTableRow]);

  useEffect(() => {
    fetchLogsRef.current = fetchLogs;
  }, [fetchLogs]);

  useEffect(() => {
    if (!ordering) {
      return;
    }
    fetchLogs();
  }, [fetchLogs, ordering]);

  const columns = useMemo(
    () => [
      {
        title: t("TASK_ID"),
        key: "id",
      },
      {
        title: t("PARENT_TASK_ID"),
        key: "parent_task",
      },
      {
        title: t("CREATED_AT"),
        key: "created_at",
        sorted: true,
      },
      {
        title: t("LAST_UPDATE"),
        key: "last_update",
      },
      {
        title: t("TASK"),
        key: "task",
      },
      {
        title: t("PAYLOAD"),
        key: "payload",
      },
      {
        title: t("RESOURCE"),
        key: "resource",
      },
      {
        title: t("STATE"),
        key: "state",
      },
      {
        title: "",
        key: "props",
      },
    ],
    [t]
  );

  const hasData = logs.length > 0;

  return (
    <Container>
      {pageLoading ? <TopProgress loading={pageLoading} /> : null}
      <WrapperRow>
        <Title>{t("PERIODIC_TASK_LOG")}</Title>
      </WrapperRow>
      {filterOptions ? (
        <WrapperRow style={{ marginTop: "12px" }}>
          <FilterBar filterOptions={filterOptions} />
        </WrapperRow>
      ) : null}
      {pageLoading ? (
        <WrapperRow style={{ flexDirection: "column", marginTop: "20px" }}>
          <TableSkeleton
            rows={10}
            columns={columns.length}
            columnsData={columns}
          />
        </WrapperRow>
      ) : hasData ? (
        <WrapperRow style={{ flexDirection: "column", marginTop: "20px" }}>
          <Table
            columns={columns}
            rows={logs}
            hideCheckbox
            paginationData={paginationData}
            updateData={fetchLogs}
          />
        </WrapperRow>
      ) : (
        <WrapperRow style={{ marginTop: "20px" }}>
          <BodyText>{t("NO_PERIODIC_TASK_LOGS")}</BodyText>
        </WrapperRow>
      )}
    </Container>
  );
};

export default PeriodicTaskLogPage;
